import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const res = await login(email, password, totp);
    setBusy(false);
    if (res.ok) { nav("/admin"); return; }
    if (res.requires2fa) { setNeeds2fa(true); setErr(""); return; }
    setErr(res.error);
  };

  return (
    <>
      <SEO title="Espace administrateur — venteaterme.fr" description="Accès administrateur" />
      <section className="min-h-[70vh] flex items-center justify-center py-24 px-6">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md bg-white border border-navy/10 p-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-3">Administration</div>
          <h1 className="font-serif text-3xl text-navy mb-8">Connexion</h1>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Email</Label>
              <Input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 border-navy/20" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Mot de passe</Label>
              <Input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 border-navy/20" />
            </div>
            {needs2fa && (
              <div className="border-t border-navy/10 pt-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">
                  <ShieldCheck size={14} /> Vérification en 2 étapes
                </div>
                <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Code à 6 chiffres (ou code de secours)</Label>
                <Input
                  data-testid="login-totp"
                  autoFocus
                  required
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  placeholder="123456"
                  className="mt-2 border-navy/20 tracking-widest"
                  inputMode="text"
                />
                <p className="text-[11px] text-navy/50 mt-2">Ouvrez votre application d'authentification (Google Authenticator, 1Password, Authy…) et saisissez le code.</p>
              </div>
            )}
            {err && <div data-testid="login-error" className="text-sm text-destructive">{err}</div>}
            <button disabled={busy} data-testid="login-submit" className="w-full bg-accent-brand text-white py-3 font-bold tracking-wide hover:bg-[#B08D22] transition-colors disabled:opacity-60">
              {busy ? "Connexion..." : needs2fa ? "Vérifier et se connecter" : "Se connecter"}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
